import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

export type ReaderTheme = 'light' | 'sepia' | 'dark'
export type ReaderSettings = {
  theme: ReaderTheme
  fontFamily: 'serif' | 'sans'
  fontSize: number // px
  lineHeight: number // px
}

const DEFAULTS: ReaderSettings = {
  theme: 'dark',
  fontFamily: 'serif',
  fontSize: 18,
  lineHeight: 28,
}

type Ctx = {
  settings: ReaderSettings
  setSettings: (next: Partial<ReaderSettings>) => void
  reset: () => void
}

const ReaderSettingsContext = createContext<Ctx | null>(null)

const LS_KEY = 'reader:settings:v1'

export function ReaderSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettingsState] = useState<ReaderSettings>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY)
      if (raw) return { ...DEFAULTS, ...(JSON.parse(raw) as any) }
    } catch {}
    return DEFAULTS
  })

  const setSettings = (next: Partial<ReaderSettings>) => {
    setSettingsState((prev) => ({ ...prev, ...next }))
  }
  const reset = () => setSettingsState(DEFAULTS)

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(settings))
    } catch {}
  }, [settings])

  const value = useMemo<Ctx>(() => ({ settings, setSettings, reset }), [settings])
  return <ReaderSettingsContext.Provider value={value}>{children}</ReaderSettingsContext.Provider>
}

export function useReaderSettings() {
  const ctx = useContext(ReaderSettingsContext)
  if (!ctx) throw new Error('useReaderSettings must be used within ReaderSettingsProvider')
  return ctx
}

